var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Button, Input, Select, Switch } from 'antd';
import { useFormik } from 'formik';
import cloneDeep from 'lodash/cloneDeep';
import React from 'react';
import * as Yup from 'yup';
import localize from '../../../../../core/utils/translation-files';
import ConditionalViewFormField from '../../../../../layout/components/conditionalViewFormField';
import Asterik from '../../../shared/helper-components/Asterik';
import { notifications } from '../../../../../layout/components/notifications';
import UseToggleStateOnScroll from '../../../../../core/hooks/useToggleStateOnScroll';
import { DEFAULT_DATA_FREQUENCY, DEFAULT_MANUAL_INTEGRATION_SYSTEM, INTEGRATION_SYSTEM_REQUIRED_VALIDATION, INTEGRATION_SYSTEM_SWITCH_FIELD, NOURISH_INTEGRATION_SYSTEM, NOURISH_INTEGRATION_TOKEN_FIELD_CODE } from '../../../../../core/constants/integrationFormFieldsConstants';
const Option = Select.Option;
import LookupDropDown from "../../../../../layout/components/select/LookupDropDown";
import { LookupNames } from '../../../../../core/constants/lookup';
const IntegrationFormFields = ({ isReadOnly, parentValues, onChange, activeIntegration, setActiveIntegration, activeIntegrationSystemTypes, activeFormFields, }) => {
    const activeFields = cloneDeep(activeFormFields); // make clone to avoid state immutability
    // Dynamically generate initialValues
    const filteredCredentials = parentValues.integrationCredentials
        .find((item) => item.platform === activeIntegration.integrationSystem && item.integrationType === (activeIntegration === null || activeIntegration === void 0 ? void 0 : activeIntegration.integrationType));
    const initialValues = activeFields.reduce((acc, field) => {
        var _a;
        acc[field.code] = ((_a = filteredCredentials === null || filteredCredentials === void 0 ? void 0 : filteredCredentials.credentials) === null || _a === void 0 ? void 0 : _a[field.code]) || '';
        return acc;
    }, {});
    // Dynamically generate validationSchema
    const validationSchema = Yup.object(activeFields.reduce((acc, field) => {
        const isRequired = field.fieldValidations &&
            field.fieldValidations.length > 0 &&
            field.fieldValidations.some(validation => validation.code.includes(INTEGRATION_SYSTEM_REQUIRED_VALIDATION));
        if (isRequired) {
            // If required, set it as Yup.string().required()
            acc[field.code] = Yup.string().required(`${field.text} is required.`);
        }
        else {
            // If not required, set it as Yup.string().notRequired()
            acc[field.code] = Yup.string().notRequired();
        }
        return acc;
    }, {}));
    const checkIsSwitchField = (field) => {
        return field.fieldValidations && field.fieldValidations.length > 0 &&
            field.fieldValidations.some(validation => validation.code.includes(INTEGRATION_SYSTEM_SWITCH_FIELD));
    };
    const handleRemoveClick = (e, itemCode) => {
        e.preventDefault();
        e.stopPropagation();
        const updatedCredentials = parentValues.integrationCredentials.filter((cred) => !(cred.platform === itemCode && cred.integrationType === activeIntegration.integrationType));
        if (activeIntegration.integrationSystem === itemCode) {
            setActiveIntegration({});
        }
        onChange("integrationCredentials", updatedCredentials);
    };
    const isEncryptedPlaceholder = (value) => {
        if (typeof value !== 'string' || value.length === 0) {
            return false;
        }
        return /^\*+$/.test(value);
    };
    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(false);
            const newCred = {
                platform: activeIntegration.integrationSystem,
                system: activeIntegration.systemName,
                integrationType: activeIntegration.integrationType,
                dataFrequency: activeIntegration.dataFrequency,
                credentials: values,
                allowRawApiAccess: activeIntegration.allowRawApiAccess,
                allowKpiProcessingLayerAccess: activeIntegration.allowKpiProcessingLayerAccess
            };
            const existing = parentValues.integrationCredentials || [];
            const idx = existing.findIndex((c) => c.platform === activeIntegration.integrationSystem && c.integrationType === activeIntegration.integrationType);
            let updatedArray = [];
            if (idx > -1) {
                const existingItem = existing[idx];
                const merged = Object.assign(Object.assign({}, existingItem), newCred);
                updatedArray = [
                    ...existing.slice(0, idx),
                    merged,
                    ...existing.slice(idx + 1),
                ];
            }
            else {
                updatedArray = [...existing, newCred];
            }
            onChange('integrationCredentials', updatedArray);
            // setActiveIntegration(prev => ({ ...prev, integrationType: DEFAULT_MANUAL_INTEGRATION_SYSTEM }));
            setActiveIntegration({});
        }
    });
    const { values, errors, touched, handleSubmit, setFieldValue, handleBlur } = formik;
    const generateIntegrationTokenForNoursih = (fieldCode) => {
        const externalReference = String(parentValues.externalReference || '');
        // Calculate how many random characters we need (36 - externalReference length)
        const randomLength = Math.abs(36 - externalReference.length);
        // Generate random alphanumeric string
        const randomString = Array(randomLength)
            .fill(0)
            .map(() => Math.random().toString(36).charAt(2))
            .join('');
        // Combine without hyphen
        const integrationToken = `${externalReference}${randomString}`;
        // Assign to Formik field
        formik.setFieldValue(fieldCode, integrationToken);
    };
    const copyFieldValueToClipboard = (fieldCode) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const fieldValue = formik.values[fieldCode];
            if (!fieldValue) {
                console.warn(`Field ${fieldCode} is empty - nothing to copy`);
                return false;
            }
            if (navigator.clipboard && navigator.clipboard.writeText) {
                yield navigator.clipboard.writeText(fieldValue);
                notifications.info("Integration Token copied to clipboard successfully");
                return true;
            }
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = fieldValue;
            textArea.style.position = 'fixed'; // Prevent scrolling to bottom
            document.body.appendChild(textArea);
            textArea.select();
            const success = document.execCommand('copy');
            document.body.removeChild(textArea);
            notifications.info("Integration Token copied to clipboard successfully");
            if (!success)
                throw new Error('Fallback copy failed');
            return true;
        }
        catch (error) {
            console.error('Failed to copy:', error);
        }
    });
    const { visible, setVisible } = UseToggleStateOnScroll();
    return (React.createElement("section", { className: "box-section" },
        React.createElement("div", { className: "box-description" },
            React.createElement("form", { onSubmit: handleSubmit },
                React.createElement("section", { className: "box-section" },
                    React.createElement("div", { className: "box-title" },
                        React.createElement("div", { className: "box-heading system-title" }, localize([activeIntegration === null || activeIntegration === void 0 ? void 0 : activeIntegration.systemName]))),
                    React.createElement("div", { className: "box-description" },
                        React.createElement("div", { className: "row" },
                            React.createElement("div", { className: "col-lg-3 col-md-4 col-sm-6" },
                                React.createElement("div", { className: "form-group" },
                                    React.createElement("div", { className: "ant-col bold-text" },
                                        React.createElement("label", null,
                                            localize(["Integration Type"]),
                                            " ",
                                            React.createElement(Asterik, null))),
                                    React.createElement(Select, { id: "integrationType", "data-testid": "single-select", allowClear: true, showSearch: true, className: `"mb-4"`, defaultValue: activeIntegration.integrationType, value: activeIntegration.integrationType, placeholder: localize(["selectAn", "Integration System Type"]), optionFilterProp: "children", onChange: (value) => setActiveIntegration(prev => (Object.assign(Object.assign({}, prev), { integrationType: value !== null && value !== void 0 ? value : DEFAULT_MANUAL_INTEGRATION_SYSTEM }))), open: visible, onDropdownVisibleChange: (open) => setVisible(open), key: `select-integrationType-${activeIntegration.integrationType}` }, activeIntegrationSystemTypes.map((option, key) => (React.createElement(Option, { key: option["code"], value: option["code"], id: "option-div-container" }, option["text"])))))),
                            React.createElement("div", { className: "col-lg-3 col-md-4 col-sm-6" },
                                React.createElement("div", { className: "form-group" },
                                    React.createElement("div", { className: "ant-col bold-text" },
                                        React.createElement("label", null,
                                            localize(["Data Frequency"]),
                                            " ",
                                            React.createElement(Asterik, null))),
                                    React.createElement(LookupDropDown, { className: "mb-2 mb-md-0", selectedValue: activeIntegration.dataFrequency, inputName: "dataFrequency", localizationKey: ["TrackedItem.dataCollectionFrequency"], lookUpType: LookupNames.LKReportTypeDataFrequency, onChange: (fieldName, value) => {
                                            console.log(`Dispatched Field ${fieldName} & value: ${value}`);
                                            setActiveIntegration(prev => (Object.assign(Object.assign({}, prev), { dataFrequency: value !== null && value !== void 0 ? value : DEFAULT_DATA_FREQUENCY })));
                                        }, error: errors &&
                                            errors['dataFrequency'] &&
                                            touched &&
                                            touched['dataFrequency'] && (React.createElement("p", { className: "error" }, errors && errors['dataFrequency'])) }))),
                            React.createElement("div", { className: "col-lg-3 col-md-4 col-sm-6", "data-test": "group-company-holding-accounts" },
                                React.createElement("div", { className: "form-group" },
                                    React.createElement("div", { className: "ant-col ant-form-item-label mr-3 mt-1" },
                                        React.createElement("label", null, "Raw API Access")),
                                    React.createElement(Switch, { className: "ml-1 mb-1", checked: activeIntegration.allowRawApiAccess, onChange: (checked, event) => setActiveIntegration(prev => (Object.assign(Object.assign({}, prev), { allowRawApiAccess: checked }))) }))),
                            React.createElement("div", { className: "col-lg-3 col-md-4 col-sm-6", "data-test": "group-company-holding-accounts" },
                                React.createElement("div", { className: "form-group" },
                                    React.createElement("div", { className: "ant-col ant-form-item-label mr-3 mt-1" },
                                        React.createElement("label", null, "KPI Processing Layer Access")),
                                    React.createElement(Switch, { className: "ml-1 mb-1", checked: activeIntegration.allowKpiProcessingLayerAccess, onChange: (checked, event) => setActiveIntegration(prev => (Object.assign(Object.assign({}, prev), { allowKpiProcessingLayerAccess: checked }))) }))),
                            activeFields.map((field) => {
                                var _a, _b, _c;
                                const isNourishIntegrationToken = activeIntegration.integrationSystem === NOURISH_INTEGRATION_SYSTEM && (field.code === NOURISH_INTEGRATION_TOKEN_FIELD_CODE);
                                const isFieldRequired = (_b = (_a = validationSchema.describe().fields[field.code]) === null || _a === void 0 ? void 0 : _a.tests) === null || _b === void 0 ? void 0 : _b.some(test => test.name === 'required');
                                const isSwitchField = checkIsSwitchField(field);
                                if (isSwitchField) {
                                    return React.createElement("div", { className: "col-lg-3 col-md-4 col-sm-6", "data-test": "group-company-holding-accounts" },
                                        React.createElement("div", { className: "form-group" },
                                            React.createElement("div", { className: "ant-col ant-form-item-label mr-3 mt-1" },
                                                React.createElement("label", null, field.text)),
                                            React.createElement(Switch, { className: "ml-1 mb-1", checked: values[field.code], onChange: (checked, event) => setFieldValue(field.code, checked) })));
                                }
                                else {
                                    return (React.createElement("div", { className: `${isNourishIntegrationToken ? 'col-lg-6 col-md-6 col-sm-12 form_input_and_button_field' : 'col-lg-3 col-md-4 col-sm-6'}`, key: field.code },
                                        React.createElement("div", { className: `${isNourishIntegrationToken && 'col-lg-6'} form-group` },
                                            React.createElement("div", { className: "ant-col bold-text" },
                                                React.createElement("label", null,
                                                    localize([field.text]),
                                                    " ",
                                                    React.createElement(Asterik, { hidden: !isFieldRequired }))),
                                            React.createElement(ConditionalViewFormField, { isViewMode: isReadOnly, labelText: values[field.text] },
                                                React.createElement(Input, { name: field.code, placeholder: `Enter ${field.text}`, id: field.code, value: values[field.code], onBlur: handleBlur, readOnly: isNourishIntegrationToken, onChange: (e) => {
                                                        setFieldValue(e.target.name, e.target.value);
                                                    }, onKeyDown: (e) => {
                                                        const currentValue = e.currentTarget.value;
                                                        const key = e.key;
                                                        if (key === 'Backspace' || key === 'Delete') {
                                                            if (isEncryptedPlaceholder(currentValue) || currentValue === '') {
                                                                setFieldValue(e.currentTarget.name, '');
                                                            }
                                                        }
                                                    }, onKeyPress: (e) => {
                                                        const currentValue = e.currentTarget.value;
                                                        if (isEncryptedPlaceholder(currentValue)) {
                                                            setFieldValue(e.currentTarget.name, '');
                                                        }
                                                    }, onPaste: (e) => {
                                                        const currentValue = e.currentTarget.value;
                                                        if (isEncryptedPlaceholder(currentValue)) {
                                                            setFieldValue(e.currentTarget.name, '');
                                                        }
                                                    }, className: `${errors &&
                                                        errors[field.code] &&
                                                        touched &&
                                                        touched[field.code]
                                                        ? "input-error"
                                                        : null}` }),
                                                errors &&
                                                    errors[field.code] &&
                                                    touched &&
                                                    touched[field.code] && (React.createElement("p", { className: "error" }, errors && errors[field.code])))),
                                        isNourishIntegrationToken &&
                                            React.createElement(React.Fragment, null,
                                                (((_c = values[field.code]) === null || _c === void 0 ? void 0 : _c.length) != 0 && !isEncryptedPlaceholder(values[field.code])) &&
                                                    React.createElement(Button, { type: "default", htmlType: "button", onClick: () => copyFieldValueToClipboard(field.code) }, localize(["Button.copy"])),
                                                React.createElement(Button, { type: "primary", htmlType: "button", onClick: () => generateIntegrationTokenForNoursih(field.code) }, localize(["Button.generate"])))));
                                }
                            })),
                        React.createElement(Button, { type: "default", htmlType: "button", onClick: (e) => {
                                e.preventDefault();
                                formik.setTouched({});
                                formik.setErrors({});
                                formik.resetForm();
                                setTimeout(() => {
                                    setActiveIntegration({});
                                }, 0);
                            } }, localize(["Button.cancel"])),
                        React.createElement(Button, { type: "primary", htmlType: "button", onClick: () => handleSubmit() }, localize((filteredCredentials === null || filteredCredentials === void 0 ? void 0 : filteredCredentials.id) ? ["Button.update"] : ["Button.add"])),
                        React.createElement(Button, { type: "default", onClick: (e) => handleRemoveClick(e, activeIntegration.integrationSystem) }, localize(["Button.remove"]))))))));
};
export default IntegrationFormFields;
